import { type RefObject, useEffect, useMemo, useRef, useState } from "react";
import { QuoteApi } from "./api";
import { DepartmentHomeCountry } from "./departmentFields";
import { fallbackConfig } from "./defaults";
import { InvalidFieldFocus } from "./fieldFocus";
import type { QuoteCopy } from "./i18n/QuoteCopy";
import type { DepartmentConfig, ManualPage, Outcome, View } from "./types";
import type { Errors } from "./validation";

const homeCountries = new DepartmentHomeCountry();

function keepCountrySlug(config: DepartmentConfig, requested: string): DepartmentConfig {
  const slug = requested.trim().toLowerCase();
  if (!homeCountries.code(slug)) return config;
  return { ...config, slug };
}

export function useDepartmentConfig(api: QuoteApi, department: string): DepartmentConfig | null {
  const [config, setConfig] = useState<DepartmentConfig | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!api.configured) {
        setConfig(keepCountrySlug(fallbackConfig(department), department));
        return;
      }
      try {
        const cfg = await api.getDepartmentConfig(department);
        if (!cancelled) setConfig(keepCountrySlug(cfg ?? fallbackConfig(department), department));
      } catch {
        if (!cancelled) setConfig(keepCountrySlug(fallbackConfig(department), department));
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [api, department]);

  return config;
}

// A field's error becomes visible once it was touched, or all at once after a
// submit attempt.
export function useGatedErrors(all: Errors, showAll: boolean, touched: Set<string>): Errors {
  return useMemo(() => {
    const out: Errors = {};
    for (const id in all) {
      if (showAll || touched.has(id)) out[id] = all[id];
    }
    return out;
  }, [all, showAll, touched]);
}

// Announces step changes to assistive tech and scrolls/focuses the active
// step heading. Skips the initial render so page load doesn't jump.
function formStepMessage(page: ManualPage, copy: QuoteCopy): string {
  if (page === "route") return copy.t("announce.route");
  if (page === "shipment") return copy.t("announce.shipment");
  if (page === "services") return copy.t("announce.services");
  return copy.t("announce.contact");
}

export function useInvalidFieldFocus(
  rootRef: RefObject<HTMLDivElement | null>,
  fieldId: string | null,
  clear: () => void,
) {
  const clearRef = useRef(clear);
  clearRef.current = clear;

  useEffect(() => {
    if (!fieldId) return;
    const root = rootRef.current;
    if (!root) return;
    let raf2 = 0;
    const raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        new InvalidFieldFocus(root).apply(fieldId);
        clearRef.current();
      });
    });
    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
    };
  }, [rootRef, fieldId]);
}

export function useStepAnnouncement(
  rootRef: RefObject<HTMLDivElement | null>,
  view: View,
  outcome: Outcome | null,
  manualPage: ManualPage,
  copy: QuoteCopy,
  holdScroll = false,
) {
  const [announce, setAnnounce] = useState("");
  const firstRender = useRef(true);
  const holdScrollRef = useRef(holdScroll);
  holdScrollRef.current = holdScroll;

  useEffect(() => {
    const attempt = outcome?.attempt ?? 0;
    const message =
      view === "choose"
        ? copy.t("announce.choose")
        : view === "quickfill"
          ? copy.t("announce.quickfill")
          : view === "form"
            ? formStepMessage(manualPage, copy)
            : view === "review"
              ? copy.t("announce.review")
              : outcome?.status === "success"
                ? copy.t("announce.received")
                : attempt > 1
                  ? copy.t("announce.sendFailedAttempt", { n: attempt })
                  : copy.t("announce.sendFailed");
    setAnnounce(message);

    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    const root = rootRef.current;
    if (!root) return;
    if (holdScrollRef.current) return;
    const resetScroll = () => {
      root.querySelectorAll(".nv-card-scroll").forEach((el) => {
        el.scrollTop = 0;
      });
    };
    resetScroll();
    let raf2 = 0;
    const raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        resetScroll();
        root.querySelector<HTMLElement>("[data-step-heading]")?.focus({ preventScroll: true });
      });
    });
    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
    };
  }, [rootRef, view, manualPage, outcome?.status, outcome?.attempt, copy]);

  return { announce, setAnnounce };
}

// Briefly highlights a set of field ids (Quick Fill flash).
export function useTransientHighlight(durationMs = 1800) {
  const [highlighted, setHighlighted] = useState<Set<string>>(new Set());
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const flash = (ids: string[]) => {
    if (!ids.length) return;
    setHighlighted(new Set(ids));
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setHighlighted(new Set()), durationMs);
  };

  const reset = () => setHighlighted(new Set());

  return { highlighted, flash, reset };
}
